// src/hooks/useRegieSync.ts
// Hook de synchronisation bidirectionnelle entre onglets/tablettes via WebSocket.
// Remplace useBroadcastSync pour la communication inter-onglets.
//
// Usage :
//   const sync = useRegieSync(uuid, wsUrl);
//
//   // Envoyer un changement :
//   sync.send('selectedVideoSource', 'camera-1');
//   sync.sendBatch({ selectedVideoSource: 'camera-1', deckPreviewSourceId: 'camera-2' });
//
//   // Recevoir les changements (via callback) :
//   useEffect(() => {
//     sync.onStateUpdate((key, value) => {
//       if (key === 'selectedVideoSource') setSelectedVideoSource(value);
//     });
//   }, []);
//
//   // Ou via snapshot complet :
//   sync.onSnapshot((state) => { /* appliquer tout l'état */ });

import { useRef, useCallback, useEffect, useState } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RegieSyncMessage {
  type: string;
  key?: string;
  value?: any;
  updates?: Record<string, any>;
  state?: Record<string, any>;
  count?: number;
}

type StateUpdateCallback = (key: string, value: any) => void;
type SnapshotCallback = (state: Record<string, any>) => void;
type ClientCountCallback = (count: number) => void;

export interface UseRegieSyncReturn {
  /** Envoie un changement d'état unique */
  send: (key: string, value: any) => void;
  /** Envoie plusieurs changements d'état en batch */
  sendBatch: (updates: Record<string, any>) => void;
  /** Enregistre un callback pour les mises à jour individuelles */
  onStateUpdate: (cb: StateUpdateCallback) => void;
  /** Enregistre un callback pour le snapshot initial */
  onSnapshot: (cb: SnapshotCallback) => void;
  /** Enregistre un callback pour le nombre de clients */
  onClientCount: (cb: ClientCountCallback) => void;
  /** Nombre de clients connectés à la room */
  clientCount: number;
  /** État de la connexion WS */
  connected: boolean;
  /** Déconnexion manuelle */
  disconnect: () => void;
}

// ── Configuration ─────────────────────────────────────────────────────────────

const RECONNECT_DELAY_MS = 2000;
const PING_INTERVAL_MS = 15000;
const MAX_RECONNECT_ATTEMPTS = 50;

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useRegieSync(uuid: string | undefined, wsUrl: string): UseRegieSyncReturn {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const manualDisconnect = useRef(false);

  const stateUpdateCbRef = useRef<StateUpdateCallback | null>(null);
  const snapshotCbRef = useRef<SnapshotCallback | null>(null);
  const clientCountCbRef = useRef<ClientCountCallback | null>(null);

  const [clientCount, setClientCount] = useState(0);
  const [connected, setConnected] = useState(false);

  // ── Enregistrement des callbacks ──────────────────────────────────────────

  const onStateUpdate = useCallback((cb: StateUpdateCallback) => {
    stateUpdateCbRef.current = cb;
  }, []);

  const onSnapshot = useCallback((cb: SnapshotCallback) => {
    snapshotCbRef.current = cb;
  }, []);

  const onClientCount = useCallback((cb: ClientCountCallback) => {
    clientCountCbRef.current = cb;
  }, []);

  // ── Traitement des messages entrants ──────────────────────────────────────

  const handleMessage = useCallback((msg: RegieSyncMessage) => {
    switch (msg.type) {
      case 'snapshot':
        if (msg.state && snapshotCbRef.current) {
          snapshotCbRef.current(msg.state);
        }
        break;

      case 'state_update':
        if (msg.key !== undefined && stateUpdateCbRef.current) {
          stateUpdateCbRef.current(msg.key, msg.value);
        }
        break;

      case 'state_batch':
        if (msg.updates && stateUpdateCbRef.current) {
          for (const [key, value] of Object.entries(msg.updates)) {
            stateUpdateCbRef.current(key, value);
          }
        }
        break;

      case 'client_count':
        if (msg.count !== undefined) {
          setClientCount(msg.count);
          clientCountCbRef.current?.(msg.count);
        }
        break;

      case 'pong':
        // Heartbeat OK
        break;

      default:
        break;
    }
  }, []);

  // ── Connexion WebSocket ───────────────────────────────────────────────────

  const connectWs = useCallback(() => {
    if (!uuid || !wsUrl || manualDisconnect.current || !mountedRef.current) return;

    // Fermer la connexion existante
    if (wsRef.current) {
      try { wsRef.current.close(); } catch {}
      wsRef.current = null;
    }

    // HAProxy attend /{uuid}/regie/{endpoint} (uuid en premier, groupe "regie"
    // ensuite) — c'est ce qu'il réécrit en /{endpoint} avant de forwarder au
    // microservice. cf. haproxy.cfg broadcastingChannelMicroservice_backend
    // et la map allowed_broadcastingChannelMicroservice_endpoints ("sync").
    const fullUrl = `${wsUrl}/${uuid}/regie/sync`;
    console.log(`[RegieSync] Connexion → ${fullUrl}`);

    try {
      const ws = new WebSocket(fullUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) { ws.close(); return; }
        console.log(`[RegieSync] Connecté (room=${uuid})`);
        reconnectAttempts.current = 0;
        setConnected(true);

        // Démarrer le ping heartbeat
        if (pingTimer.current) clearInterval(pingTimer.current);
        pingTimer.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL_MS);
      };

      ws.onmessage = (event) => {
        try {
          const msg: RegieSyncMessage = JSON.parse(event.data);
          handleMessage(msg);
        } catch (e) {
          console.warn('[RegieSync] Message JSON invalide:', event.data);
        }
      };

      ws.onclose = (event) => {
        setConnected(false);
        if (pingTimer.current) { clearInterval(pingTimer.current); pingTimer.current = null; }

        if (manualDisconnect.current || !mountedRef.current) {
          console.log('[RegieSync] Déconnexion volontaire');
          return;
        }

        console.log(`[RegieSync] Connexion fermée (code=${event.code}), reconnexion...`);
        scheduleReconnect();
      };

      ws.onerror = (event) => {
        console.error('[RegieSync] Erreur WebSocket');
      };

    } catch (e) {
      console.error('[RegieSync] Erreur création WebSocket:', e);
      scheduleReconnect();
    }
  }, [uuid, wsUrl, handleMessage]);

  const scheduleReconnect = useCallback(() => {
    if (manualDisconnect.current || !mountedRef.current) return;
    if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
      console.error('[RegieSync] Max tentatives de reconnexion atteint');
      return;
    }

    reconnectAttempts.current++;
    const delay = Math.min(RECONNECT_DELAY_MS * Math.pow(1.3, reconnectAttempts.current - 1), 30000);
    console.log(`[RegieSync] Reconnexion dans ${Math.round(delay)}ms (tentative ${reconnectAttempts.current})`);

    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    reconnectTimer.current = setTimeout(() => {
      if (mountedRef.current && !manualDisconnect.current) connectWs();
    }, delay);
  }, [connectWs]);

  // ── Envoi de messages ─────────────────────────────────────────────────────

  const send = useCallback((key: string, value: any) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ type: 'state_update', key, value }));
    } catch (e) {
      console.warn('[RegieSync] Erreur envoi:', e);
    }
  }, []);

  const sendBatch = useCallback((updates: Record<string, any>) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ type: 'state_batch', updates }));
    } catch (e) {
      console.warn('[RegieSync] Erreur envoi batch:', e);
    }
  }, []);

  // ── Déconnexion manuelle ──────────────────────────────────────────────────

  const disconnect = useCallback(() => {
    manualDisconnect.current = true;
    if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null; }
    if (pingTimer.current) { clearInterval(pingTimer.current); pingTimer.current = null; }
    if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
    setConnected(false);
  }, []);

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  useEffect(() => {
    mountedRef.current = true;
    manualDisconnect.current = false;
    connectWs();

    return () => {
      mountedRef.current = false;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (pingTimer.current) clearInterval(pingTimer.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
    };
  }, [connectWs]);

  return {
    send,
    sendBatch,
    onStateUpdate,
    onSnapshot,
    onClientCount,
    clientCount,
    connected,
    disconnect,
  };
}

export default useRegieSync;
